import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import React, { useRef, useState } from "react";
import styled from "styled-components";

import { useBulkUpdateResolutionStatusMutation } from "../../store/queries/claims";
import {
  IClaim,
  ResolutionStatus,
  RESOLUTION_STATUS_LABELS,
} from "../../types/claim";

interface IBulkEditStatusModalProps {
  visible: boolean;
  onHide: () => void;
  selectedClaims: IClaim[];
  onSuccess: () => void;
}

// Mirrors the dropdown in AIInsightsTabContent — same labels, same option set.
const StatusOptions = [
  {
    label: RESOLUTION_STATUS_LABELS[ResolutionStatus.IN_REVIEW],
    value: ResolutionStatus.IN_REVIEW,
  },
  {
    label: RESOLUTION_STATUS_LABELS[ResolutionStatus.SUBMITTED],
    value: ResolutionStatus.SUBMITTED,
  },
  {
    label: RESOLUTION_STATUS_LABELS[ResolutionStatus.CLOSED],
    value: ResolutionStatus.CLOSED,
  },
  {
    label: RESOLUTION_STATUS_LABELS[ResolutionStatus.NO_APPEAL_NEEDED],
    value: ResolutionStatus.NO_APPEAL_NEEDED,
  },
];

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-width: 28rem;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 0.875rem;
`;

/**
 * CLE-2172: confirm-and-apply modal for bulk resolution-status update.
 *
 * The single-claim equivalent lives in AIInsightsTabContent.tsx — same option
 * set and same optional notes textarea, but applied across N claims at once.
 * Server-side this is transactional (all-or-nothing), so the response always
 * has `failureCount: 0` on the 200 path; failures surface via the error toast.
 */
export function BulkEditStatusModal({
  visible,
  onHide,
  selectedClaims,
  onSuccess,
}: IBulkEditStatusModalProps) {
  const toastRef = useRef<Toast>(null);
  const [chosenStatus, setChosenStatus] = useState<ResolutionStatus | null>(
    null
  );
  const [notes, setNotes] = useState("");

  const [bulkUpdateStatus, { isLoading: isApplying }] =
    useBulkUpdateResolutionStatusMutation();

  const handleApply = async () => {
    if (!chosenStatus) return;

    try {
      const result = await bulkUpdateStatus({
        claimIds: selectedClaims.map((c) => c.id),
        status: chosenStatus,
        notes: notes.trim() || undefined,
      }).unwrap();

      toastRef.current?.show({
        severity: "success",
        summary: "Bulk status update",
        detail: `${result.successCount} claim${
          result.successCount === 1 ? "" : "s"
        } changed to "${RESOLUTION_STATUS_LABELS[chosenStatus]}".`,
        life: 5000,
      });

      setChosenStatus(null);
      setNotes("");
      onSuccess();
    } catch (err: any) {
      toastRef.current?.show({
        severity: "error",
        summary: "Bulk status update failed",
        detail:
          err?.data?.error || err?.data?.messages?.[0] || "Request failed.",
        life: 8000,
      });
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        onClick={onHide}
        disabled={isApplying}
      />
      <Button
        label={`Apply to ${selectedClaims.length}`}
        icon="pi pi-check"
        onClick={handleApply}
        disabled={!chosenStatus || isApplying}
        loading={isApplying}
      />
    </div>
  );

  return (
    <>
      <Toast ref={toastRef} />
      <Dialog
        header="Update resolution status"
        visible={visible}
        onHide={isApplying ? () => {} : onHide}
        closable={!isApplying}
        footer={footer}
      >
        <Body>
          <div>
            Change resolution status on <strong>{selectedClaims.length}</strong>{" "}
            selected claim
            {selectedClaims.length === 1 ? "" : "s"}.
          </div>

          <div>
            <Label htmlFor="bulk-status-dropdown">New status</Label>
            <Dropdown
              inputId="bulk-status-dropdown"
              value={chosenStatus}
              options={StatusOptions}
              onChange={(e) => setChosenStatus(e.value)}
              placeholder="Select a status"
              disabled={isApplying}
              style={{ width: "100%" }}
            />
          </div>

          <div>
            <Label htmlFor="bulk-status-notes">Notes (optional)</Label>
            <InputTextarea
              id="bulk-status-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Recorded in each claim's resolution history"
              rows={3}
              disabled={isApplying}
              style={{ width: "100%" }}
            />
          </div>
        </Body>
      </Dialog>
    </>
  );
}
